import {defineStore} from 'pinia'
import {ISystemMessageDto, useSystemMessageApiStore} from '../api/SystemMessageApiStore/useSystemMessageApiStore'
import {useSocketStore} from '../SocketStore/useSocketStore'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {useAuthStore} from '../AuthStore/useAuthStore'
import {computed, ref, watch} from 'vue'
import {
  ISystemMessageCreatedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/ISystemMessageCreatedEmitPayload'
import {
  ISystemMessageDeletedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/ISystemMessageDeletedEmitPayload'
import {UserSettingName} from '@toolify/server/src/models/mongoose/UserModel/enum/UserSettingName'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {IUserResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {LocaleType} from '@toolify/server/src/services/UserSettingsService/strategies/LocaleUserSettingStrategy/enum/LocaleType'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {SystemMessagePriorityType} from '@toolify/server/src/enum/SystemMessagePriorityType'

export interface ISystemMessageInstance {
  id: string
  content: string
  localeType: LocaleType
  priority: SystemMessagePriorityType
  iconNameType?: IconNameType
  publishedAt: Date | null
  expiresAt: Date | null
  createdAt: Date
}

function isMessageWithinSchedule(message: ISystemMessageInstance, atMs: number): boolean {
  if(message.publishedAt != null && message.publishedAt.getTime() > atMs) {
    return false
  }
  if(message.expiresAt != null && message.expiresAt.getTime() <= atMs) {
    return false
  }
  return true
}

function mapDtoToInstance(message: ISystemMessageDto): ISystemMessageInstance {
  return {
    id: message.id,
    content: message.content,
    localeType: message.localeType,
    priority: message.priority ?? SystemMessagePriorityType.Neutral,
    iconNameType: message.iconNameType,
    publishedAt: message.publishedAt != null ? new Date(message.publishedAt) : null,
    expiresAt: message.expiresAt != null ? new Date(message.expiresAt) : null,
    createdAt: new Date(message.createdAt),
  }
}

export const useSystemMessageStore = defineStore('systemMessage', () => {
  const systemMessagesFromApi = ref<ISystemMessageInstance[]>([])
  const activeSystemMessageId = ref<string | null>(null)
  const visibilityTick = ref(0)
  let visibilityIntervalId: ReturnType<typeof setInterval> | undefined
  let refreshIntervalId: ReturnType<typeof setInterval> | undefined
  const isLoading = ref(false)
  let fetchCurrentRequestId = 0
  const socketStore = useSocketStore()
  const authStore = useAuthStore()

  function resolveUserLocale(): LocaleType {
    const user = authStore.currentEntity?.entityType === EntityType.User
      ? authStore.currentEntity.payload as IUserResponseInstance
      : null
    return user?.settings?.[UserSettingName.Locale] ?? LocaleType.English
  }

  const visibleSystemMessages = computed<ISystemMessageInstance[]>(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    visibilityTick.value
    const locale = resolveUserLocale()
    const at = Date.now()
    return systemMessagesFromApi.value.filter((message) => {
      return message.localeType === locale && isMessageWithinSchedule(message, at)
    })
  })

  const visibleSystemMessageIndex = computed(() => {
    const messages = visibleSystemMessages.value
    if(messages.length === 0) {
      return -1
    }
    const activeIndex = messages.findIndex(message => message.id === activeSystemMessageId.value)
    return activeIndex >= 0 ? activeIndex : 0
  })

  const visibleSystemMessage = computed<ISystemMessageInstance | null>(() => {
    return visibleSystemMessages.value[visibleSystemMessageIndex.value] ?? null
  })

  const visibleSystemMessageCount = computed(() => visibleSystemMessages.value.length)

  const hasVisibleSystemMessage = computed(() => visibleSystemMessage.value != null)

  function showSystemMessageAt(index: number): void {
    const messages = visibleSystemMessages.value
    if(messages.length === 0) {
      return
    }
    const boundedIndex = ((index % messages.length) + messages.length) % messages.length
    activeSystemMessageId.value = messages[boundedIndex].id
  }

  function showNextSystemMessage(): void {
    showSystemMessageAt(visibleSystemMessageIndex.value + 1)
  }

  function showPreviousSystemMessage(): void {
    showSystemMessageAt(visibleSystemMessageIndex.value - 1)
  }

  async function fetchCurrent(): Promise<void> {
    const requestId = ++fetchCurrentRequestId
    if(!authStore.isLoggedIn) {
      systemMessagesFromApi.value = []
      activeSystemMessageId.value = null
      return
    }
    isLoading.value = true
    try {
      const api = useSystemMessageApiStore()
      const response = await api.getCurrent()
      const list = response?.systemMessages ?? []
      if(requestId !== fetchCurrentRequestId) {
        return
      }
      systemMessagesFromApi.value = list.map(mapDtoToInstance)
    } finally {
      if(requestId === fetchCurrentRequestId) {
        isLoading.value = false
      }
    }
  }

  async function acknowledgeAll(): Promise<void> {
    const systemMessageIds = visibleSystemMessages.value.map(message => message.id)
    if(systemMessageIds.length === 0) {
      return
    }
    systemMessagesFromApi.value = systemMessagesFromApi.value.filter(message => !systemMessageIds.includes(message.id))
    activeSystemMessageId.value = null
    visibilityTick.value += 1
    const api = useSystemMessageApiStore()
    try {
      await api.acknowledgeMany(systemMessageIds)
    } finally {
      await fetchCurrent()
    }
  }

  function onSystemMessageCreated(_payload: ISystemMessageCreatedEmitPayload): void {
    fetchCurrent()
  }

  function onSystemMessageDeleted(payload: ISystemMessageDeletedEmitPayload): void {
    systemMessagesFromApi.value = systemMessagesFromApi.value.filter((m) => m.id !== payload.systemMessageId)
    fetchCurrent()
  }

  function initialize(): void {
    socketStore.on(GlobalSocketEventType.SystemMessageCreated, onSystemMessageCreated)
    socketStore.on(GlobalSocketEventType.SystemMessageDeleted, onSystemMessageDeleted)

    visibilityIntervalId = setInterval(() => {
      visibilityTick.value += 1
    }, 30_000)

    refreshIntervalId = setInterval(() => {
      void fetchCurrent()
    }, 5 * 60_000)

    watch(
      () => authStore.isLoggedIn ? `${authStore.currentEntity?.entityId}:${resolveUserLocale()}` : null,
      () => {
        void fetchCurrent()
      },
      {immediate: true},
    )
  }

  function dispose(): void {
    socketStore.off(GlobalSocketEventType.SystemMessageCreated, onSystemMessageCreated)
    socketStore.off(GlobalSocketEventType.SystemMessageDeleted, onSystemMessageDeleted)
    if(visibilityIntervalId !== undefined) {
      clearInterval(visibilityIntervalId)
      visibilityIntervalId = undefined
    }
    if(refreshIntervalId !== undefined) {
      clearInterval(refreshIntervalId)
      refreshIntervalId = undefined
    }
  }

  return {
    visibleSystemMessages,
    visibleSystemMessage,
    visibleSystemMessageIndex,
    visibleSystemMessageCount,
    hasVisibleSystemMessage,
    isLoading,
    fetchCurrent,
    acknowledgeAll,
    showNextSystemMessage,
    showPreviousSystemMessage,
    initialize,
    dispose,
  }
})
